/**
 * One TruWe Consent — the single vocabulary for every opt-in surface.
 *
 * Every capture point (cookie banner, signup popup, quiz, cart, checkout,
 * account preference centre) uses these channels, this copy and this policy
 * version so the audit ledger is consistent and the customer always reads
 * the same promise.
 */

export const CONSENT_POLICY_VERSION = "2026-08-04";

export const CONSENT_CHANNELS = [
  "advertising",
  "analytics",
  "whatsapp",
  "email",
  "push",
  "sms",
  "phone",
] as const;
export type ConsentChannel = (typeof CONSENT_CHANNELS)[number];

export type ConsentStatus = "granted" | "denied";

/** The shared promise shown above the toggles, everywhere. */
export const CONSENT_HEADLINE = "Help us serve you better";
export const CONSENT_BODY =
  "Keep me updated on my order, my protocol and occasional TruWe offers. You can change this any time.";

export interface ChannelCopy {
  channel: ConsentChannel;
  label: string;
  hint: string;
}

export const CHANNEL_COPY: Record<ConsentChannel, ChannelCopy> = {
  sms: {
    channel: "sms",
    label: "SMS",
    hint: "Optional promotional text messages. Subject to applicable telecom preferences.",
  },
  phone: {
    channel: "phone",
    label: "Phone calls",
    hint: "Optional promotional calls. Subject to applicable telecom preferences.",
  },
  whatsapp: {
    channel: "whatsapp",
    label: "WhatsApp",
    hint: "Order updates, restock alerts and occasional offers.",
  },
  email: {
    channel: "email",
    label: "Email",
    hint: "Your protocol, guidance and important account notices.",
  },
  push: {
    channel: "push",
    label: "Browser notifications",
    hint: "Reminders and updates on this device or installed app.",
  },
  advertising: {
    channel: "advertising",
    label: "Personalised recommendations",
    hint: "Lets us show you products that match what you actually need.",
  },
  analytics: {
    channel: "analytics",
    label: "Analytics",
    hint: "Optional measurement of how TruWe is used, so we can improve it.",
  },
};

/** Channels offered in ordinary lead-capture surfaces. */
export const MESSAGING_CHANNELS: ConsentChannel[] = ["whatsapp", "email", "push"];

export function isConsentChannel(value: string): value is ConsentChannel {
  return (CONSENT_CHANNELS as readonly string[]).includes(value);
}
